import type { Dispatch, SetStateAction } from 'react';

import { usePostGroupAvatar } from '@repo/api/group/usePostGroupAvatar';
import { CheckCircle2, InfoIcon } from 'lucide-react';
import { useEffect } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ImageCropper, useImageCropper } from '@/components/ui/image-cropper';
import { LoadingSwap } from '@/components/ui/loading-swap';

interface GroupAvatarEditorProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  id?: number;
}

export default function GroupAvatarEditor(groupAvatarEditorProps: GroupAvatarEditorProps) {
  const { open, onOpenChange, id } = groupAvatarEditorProps;
  const createGroupAvatarMutation = usePostGroupAvatar();
  const cropperControls = useImageCropper();

  const handleCropAvatar = async () => {
    const blob: Blob | null = await cropperControls.getCroppedImage();
    if (!blob || !id) return;
    await createGroupAvatarMutation
      .mutateAsync({
        b64: blob,
        id: id,
      })
      .then(() => {
        onOpenChange(false);
        cropperControls.setImage(null);
      });
  };

  useEffect(() => {
    if (open) {
      createGroupAvatarMutation.reset();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogBody className={'grow'}>
          <DialogHeader className={'border-border-primary border-b'}>
            <DialogTitle>Edytuj awatar grupy</DialogTitle>
          </DialogHeader>
          <DialogDescription className={'sr-only'}>Wybierz zdjęcie i wykadruj je w odpowiednim miejscu.</DialogDescription>
          <ImageCropper cropperControls={cropperControls} aspectRatio={1} maxHeight={256} maxWidth={256} />
          <div className="p-4">
            <Alert
              variant={'destructive'}
              data-visible={!!createGroupAvatarMutation.isError}
              className="overflow-hidden opacity-100 transition-all duration-400 data-[visible=false]:max-h-0 data-[visible=false]:opacity-0 data-[visible=true]:h-fit data-[visible=true]:opacity-100"
            >
              <InfoIcon />
              <AlertDescription>{createGroupAvatarMutation.errorMessage.detail}</AlertDescription>
            </Alert>
          </div>
        </DialogBody>
        <DialogFooter className={'border-border-primary border-t'}>
          <Button className={'w-full md:w-fit'} type={'button'} variant={'tertiary'} onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button disabled={createGroupAvatarMutation.isPending} className={'w-full md:w-fit'} type={'button'} aria-label="submit" onClick={handleCropAvatar}>
            <LoadingSwap isLoading={createGroupAvatarMutation.isPending} className="flex items-center gap-2">
              <CheckCircle2 />
              Zapisz
            </LoadingSwap>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
